import { AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface ValidationSummaryProps {
  errors: string[];
  title?: string;
  className?: string;
}

// Use only the warning palette tokens that exist in globals.css —
// warning-50, warning-300, warning-500, warning-700. Any other shade
// (warning-100/200/400/etc) is undefined and falls back to currentColor,
// which renders as a dark outline against the warm fill — that's what
// caused the previous "heavy black border" rendering.
export function ValidationSummary({
  errors,
  title = 'Fix the following before saving',
  className,
}: ValidationSummaryProps) {
  if (errors.length === 0) return null;
  const count = errors.length;
  return (
    <div
      role="alert"
      className={cn('border-warning-300/70 bg-warning-50 rounded-xl border p-5', className)}
    >
      <div className="flex items-start gap-3.5">
        <span className="bg-warning-300/30 text-warning-700 mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
          <AlertTriangle className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h3 className="text-body-md text-warning-700 leading-snug font-medium">{title}</h3>
            <span className="bg-warning-300/40 text-warning-700 text-meta-sm rounded-full px-2 py-0.5 font-semibold tabular-nums">
              {count}
            </span>
          </div>
          <ul className="mt-3 space-y-2">
            {errors.map((err) => (
              <li
                key={err}
                className="text-body-sm text-warning-700/90 flex items-start gap-2.5 leading-relaxed"
              >
                <span
                  aria-hidden="true"
                  className="bg-warning-500/80 mt-2 h-1.5 w-1.5 shrink-0 rounded-full"
                />
                <span>{err}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
